import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { firstValueFrom, Observable, of, Subject, throwError } from "rxjs";
import { catchError, finalize, map, shareReplay } from "rxjs/operators";
// import { sEndPoint } from '.env.properties';
//set up environment variable
// export const sURL = 'https://scratch-api.aggregator.club/' + 'v1/api/';
// export const sURL = 'http://localhost:8080/' + 'v1/api/';
import { environment } from "../environments/environment";
import { Router } from "@angular/router";
import { BfCredPublicKey } from "../providers/encrypt-value";

export interface ApiResponse {
  result: any[];
  status: any;
}

@Injectable({ providedIn: "root" })
export class ApiService {
  $tokenRefreshed = new Subject<boolean>();
  $refreshToken = new Subject<boolean>();
  oRoles = null;
  oPermissions = null;

  private static readonly RENEW_SKEW_MS = 60_000;
  private renewing$: Observable<string> | undefined;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) { }

  clearUserToken() {
    this.renewing$ = undefined;
    localStorage.removeItem("app");
    localStorage.removeItem("lastActivityTime");
    localStorage.removeItem(EXPIRED_CREDENTIAL_FLAG_KEY);
    this.oPermissions = null;
  }

  get permissions() {
    const userData = this.getUserData();
    return userData.PERMISSIONS.split(",");
  }

  getRoles() {
    return this.oRoles;
  }

  saveUserToken(data: any) {
    localStorage.setItem("app", JSON.stringify(data));
    // A new token (login or renew) says for itself whether the password expired.
    localStorage.removeItem(EXPIRED_CREDENTIAL_FLAG_KEY);
    let oData = JSON.parse(atob(data.access_token.split(".")[1]));
    let oUserFields = oData.user_fields;
    this.oRoles = oUserFields.ROLES;
    this.oPermissions = oUserFields.PERMISSIONS;
  }

  getUserToken() {
    const oSessionData = localStorage.getItem("app");
    if (oSessionData != null) {
      return JSON.parse(oSessionData);
    }
    return null;
  }

  getUserData() {
    let oUserData = this.getUserToken();
    let oData = JSON.parse(atob(oUserData.access_token.split(".")[1]));
    let oUserFields = oData.user_fields;
    let id = oUserFields.ID;
    let name = oUserFields.NAME;
    let email = oUserFields.EMAIL;
    let roles = oUserFields.ROLES;
    let permissions = oUserFields.PERMISSIONS;
    let parentId = oUserFields.PARENT_ID;
    let domainId = oUserFields?.DOMAIN_ID;
    let parentRole = oUserFields.PARENT_ROLE;

    return {
      ID: id,
      NAME: name,
      EMAIL: email,
      PARENT_ID: parentId,
      ROLE: roles,
      PERMISSIONS: permissions,
      DOMAIN_ID: domainId,
      PARENT_ROLE: parentRole,
    };
  }

  /** Access-token expiry in ms: `exp` from the login/renew response (seconds), else the JWT's own `exp`. */
  private getExpiryMs(): number | null {
    const oUserData = this.getUserToken();
    if (!oUserData) {
      return null;
    }
    if (typeof oUserData.exp === "number") {
      return oUserData.exp * 1000;
    }
    try {
      const payload = oUserData.access_token.split(".")[1].replaceAll("-", "+").replaceAll("_", "/");
      const jwtExp = JSON.parse(atob(payload)).exp;
      return typeof jwtExp === "number" ? jwtExp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** True once fewer than 60 seconds of the access token's life are left. */
  isAboutToExpire(): boolean {
    const expiresAtMs = this.getExpiryMs();
    return expiresAtMs !== null && Date.now() >= expiresAtMs - ApiService.RENEW_SKEW_MS;
  }

  /**
   * Exchanges the refresh token for a new access token (`POST /v1/auth/renew`).
   * Refresh tokens are single-use, so concurrent callers share one request.
   * Emits the new access token; a 401 from the server (refresh token invalid or
   * expired) logs the user out. This is the only thing that ends a session:
   * there is deliberately no background timer, so an idle user's refresh token
   * expires on the server and the next renew fails.
   */
  renewToken(): Observable<string> {
    if (this.renewing$) {
      return this.renewing$;
    }
    const refreshToken = this.getUserToken()?.refresh_token;
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error("No refresh token"));
    }

    const renew$ = this.send2Server(environment.bURL + "/v1/auth/renew", true, null, {
      "refresh-token": refreshToken,
    }).pipe(
      map((response: any) => {
        if (!response?.access_token) {
          throw new Error("Invalid renew response");
        }
        this.saveUserToken({
          ...this.getUserToken(),
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          exp: response.exp,
          user: response.user_fields,
          password_expired: response.password_expired === true,
        });
        return response.access_token as string;
      }),
      catchError((err) => this.handleRenewError(err, refreshToken)),
      finalize(() => {
        this.renewing$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.renewing$ = renew$;
    return renew$;
  }

  private handleRenewError(err: any, sentRefreshToken: string): Observable<string> {
    if (err?.status === 401) {
      const current = this.getUserToken();
      if (current?.refresh_token && current.refresh_token !== sentRefreshToken) {
        // Another tab renewed first and stored a newer token; use that one.
        return of(current.access_token);
      }
      this.logout();
    }
    return throwError(() => err);
  }

  send2Server(url: string, bPost: boolean = false, oItem: any = null, extraHeaders?: Record<string, string | number>): Observable<any> {
    if (bPost) {
      return this.http.post<ApiResponse>(url, oItem, this.getHeaders(extraHeaders));
    }
    return this.http.get<ApiResponse>(url, this.getHeaders());
  }

  /**
   * Public key for encrypting Betfair account passwords, served by bf-rest
   * (derived there from its private key). Undefined when none is configured.
   */
  async getBfCredPublicKey(): Promise<BfCredPublicKey | undefined> {
    const response = await firstValueFrom(this.send2Server(`${environment.mURL}/bf/cred/public-key`));
    return response?.data ?? undefined;
  }

  getURL() {
    return environment.sURL;
  }
  getRiskCalculatorURL() {
    return environment.riskCalculatorURL;
  }

  getHeaders(extraHeaders?: Record<string, string | number>): any {
    let oHeaders = new HttpHeaders({
      "Content-Type": "application/json",
      ...extraHeaders,
    });
    return { headers: oHeaders };
  }

  getDomainName(): string | null {
    let hostname = window.location.hostname;
    if (!hostname) {
      return null;
    }

    const parts = hostname.split(".");
    if (parts.length >= 2) {
      return parts.slice(-2).join(".");
    }

    return hostname;
  }

  async performLogin(uname: string, pwd: string): Promise<UserDataType | null> {
    let code = localStorage.getItem("countryCode");
    let domainName = this.getDomainName();

    if (uname === "admin" && pwd === "admin123") {
      let oUserData = {
        access_token: "DEV_TOKEN",
        refresh_token: "",
        name: uname,
        domain_id: "",
        domain_name: domainName ?? "",
        is_2fa_enabled: "false",
        is_2fa_verified: "false",
        id: "",
        password_expired: false,
      };

      return oUserData;
    }
    try {
      const data: any = await firstValueFrom(
        this.send2ServerRelative("auth/admin_login_v2", true, {
          USERNAME: uname,
          PASSWORD: pwd,
          COUNTRY_CODE: code,
          DOMAIN_NAME: domainName ?? "",
        })
      );
      let oUserData = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        exp: data.exp,
        name: data.user_fields.NAME,
        domain_id: data.user_fields.DOMAIN_ID,
        domain_name: data.user_fields.DOMAIN_NAME,
        is_2fa_enabled: data.user_fields.IS_2FA_ENABLED,
        is_2fa_verified: data.user_fields.IS_2FA_VERIFIED,
        id: data.user_fields.ID,
        // Admin-side accounts must change an expired password before anything else.
        password_expired:
          data.password_expired === true || String(data.user_fields?.PASSWORD_EXPIRED).toLowerCase() === "true",
      };
      return oUserData;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  logout() {
    this.clearUserToken();
    this.router.navigate(["authentication/login"]);
  }

  send2ServerRelative(sPartialURL: string, bPost: boolean = false, oItem: any = null): Observable<any> {
    if (bPost) {
      return this.http.post<ApiResponse>(this.getURL() + sPartialURL, oItem, this.getHeaders());
    }
    return this.http.get<ApiResponse>(this.getURL() + sPartialURL, this.getHeaders());
  }
}

export interface UserDataType {
  id: string;
  access_token: string;
  refresh_token: string;
  /** Access-token expiry as a Unix timestamp in seconds. */
  exp?: number;
  name: string;
  domain_id: string;
  domain_name: string;
  is_2fa_enabled: string;
  is_2fa_verified: string;
  /** From the login response: the password must be changed before anything else. */
  password_expired?: boolean;
}

/** Page where the logged-in user changes their own password. */
export const CREDENTIAL_CHANGE_ROUTE = "/home/reset-password";

/** localStorage key set when an API call answered 403 PASSWORD_EXPIRED. */
const EXPIRED_CREDENTIAL_FLAG_KEY = "passwordExpired";

let oExpiryCache: { token: string; expired: boolean } | null = null;

/** The PASSWORD_EXPIRED flag auth-redis put in the JWT's user_fields. */
function tokenSaysPasswordExpired(sToken: string): boolean {
  try {
    const sPayload = sToken.split(".")[1].replaceAll("-", "+").replaceAll("_", "/");
    const oData = JSON.parse(atob(sPayload));
    return String(oData?.user_fields?.PASSWORD_EXPIRED).toLowerCase() === "true";
  } catch {
    return false;
  }
}

/**
 * Whether the logged-in admin's password has expired. auth-redis returns
 * `password_expired` with every login and renew (also PASSWORD_EXPIRED in the
 * token's user_fields), and refuses every API call but change-password
 * (403 PASSWORD_EXPIRED) until the password is changed; a 403 seen before the
 * session says so (expired after login) is remembered by markPasswordExpired().
 */
export function isPasswordExpired(): boolean {
  if (localStorage.getItem(EXPIRED_CREDENTIAL_FLAG_KEY) === "true") {
    return true;
  }
  const sSession = localStorage.getItem("app");
  if (!sSession) {
    return false;
  }
  try {
    const oUserData = JSON.parse(sSession);
    if (oUserData?.password_expired === true) {
      return true;
    }
    const sToken: string | undefined = oUserData?.access_token;
    if (!sToken) {
      return false;
    }
    // Checked on every change detection by the layout: decode each token once.
    if (oExpiryCache?.token !== sToken) {
      oExpiryCache = { token: sToken, expired: tokenSaysPasswordExpired(sToken) };
    }
    return oExpiryCache.expired;
  } catch {
    return false;
  }
}

/** An API call answered 403 PASSWORD_EXPIRED: treat the session as expired. */
export function markPasswordExpired() {
  localStorage.setItem(EXPIRED_CREDENTIAL_FLAG_KEY, "true");
}

/** Where to go after a successful login: change an expired password first. */
export function routeAfterLogin(): string[] {
  return isPasswordExpired() ? [CREDENTIAL_CHANGE_ROUTE] : ["home/users"];
}
